import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Pagination, Spinner, Alert, Modal, Badge, Form, Nav } from "react-bootstrap";
import ReportService from "../services/report.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const STATUS_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
];

const statusVariant = (status) => {
    switch (status) {
        case "PENDING":
            return "warning";
        case "APPROVED":
            return "success";
        case "REJECTED":
            return "danger";
        default:
            return "secondary";
    }
};

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString();
};

const TrespassRequestManager = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statusFilter, setStatusFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("registration");
    const pageSize = 50;

    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ type: "", request: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [reasonRequest, setReasonRequest] = useState(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ReportService.getTrespassRequests(
                activeTab === "registration" ? "REGISTER" : "EXEMPTION",
                statusFilter || null,
                searchTerm,
                page,
                pageSize
            );
            setRequests(response.data.content || []);
            setTotalPages(response.data.totalPages || 0);
        } catch (err) {
            console.error("Error fetching trespass requests:", err);
            setError("Failed to load trespass requests.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, page, statusFilter, searchTerm]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(0);
    };

    const handleActionClick = (type, request) => {
        setConfirmAction({ type, request });
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        const { type, request } = confirmAction;
        if (!request) return;

        setShowConfirm(false);
        setActionLoading(true);

        try {
            if (type === "delete") {
                await ReportService.deleteTrespassRequest(request.id);
                toast.success(`Request #${request.id} deleted.`);
            } else {
                const statusByAction = {
                    pending: "PENDING",
                    approve: "APPROVED",
                    reject: "REJECTED",
                };
                const status = statusByAction[type];
                await ReportService.updateTrespassRequestStatus(request.id, status);
                toast.success(`Request #${request.id} marked as ${status}.`);
            }
            fetchRequests();
        } catch (err) {
            console.error(`Error updating trespass request:`, err);
            const message = err?.response?.data || `Failed to ${type} request #${request.id}.`;
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message });
        } finally {
            setActionLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const confirmTitleByAction = {
        pending: "Set Trespass Request to Pending",
        approve: "Approve Trespass Request",
        reject: "Reject Trespass Request",
        delete: "Delete Trespass Request",
    };
    const confirmVerbByAction = {
        pending: "set to pending",
        approve: "approve",
        reject: "reject",
        delete: "permanently delete",
    };
    const confirmTitle = confirmTitleByAction[confirmAction.type] || "Confirm Action";
    const confirmBody = confirmAction.request
        ? confirmAction.type === "delete"
            ? `Are you sure you want to permanently delete request #${confirmAction.request.id} for "${confirmAction.request.shopName}" from ${confirmAction.request.clientName}?`
            : `Are you sure you want to ${confirmVerbByAction[confirmAction.type]} request #${confirmAction.request.id} for "${confirmAction.request.shopName}" from ${confirmAction.request.clientName}?`
        : "";

    if (error) {
        return <Alert variant="danger" className="mt-3">{error}</Alert>;
    }

    return (
        <div className="container-fluid py-4" style={{ background: "var(--bg-gradient)", minHeight: "calc(100vh - 76px)" }}>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <h2>Trespass Requests</h2>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <Form.Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        style={{ width: "180px" }}
                        aria-label="Filter by status"
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <option key={option.value || "all"} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                    <div className="input-group" style={{ width: "300px" }}>
                        <span className="input-group-text bg-white border-end-0">
                            <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search client name..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>
            </div>

            <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
                <Nav.Item>
                    <Nav.Link eventKey="registration">Registration</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="exemption">Exemption</Nav.Link>
                </Nav.Item>
            </Nav>

            <Table striped bordered hover responsive className="shadow-sm align-middle">
                <thead className="table-dark">
                    <tr>
                        <th>Status</th>
                        <th>Client</th>
                        <th>Workstation</th>
                        <th>Shop</th>
                        <th>Address</th>
                        <th>Contact</th>
                        {activeTab === "registration" ? <th>Credit Card</th> : <th>Reason</th>}
                        <th>Created</th>
                        <th style={{ width: "170px" }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {requests.length > 0 ? (
                        requests.map((request) => (
                            <tr key={request.id}>
                                <td>
                                    <Badge bg={statusVariant(request.status)}>{request.status}</Badge>
                                </td>
                                <td>
                                    <div className="fw-semibold">{request.clientName}</div>
                                    {request.requestedClientName && (
                                        <div className="text-muted small">Requested: {request.requestedClientName}</div>
                                    )}
                                </td>
                                <td>
                                    <div>{request.workstationName || "-"}</div>
                                    {request.workstationId && (
                                        <div className="text-muted small">ID: {request.workstationId}</div>
                                    )}
                                </td>
                                <td>
                                    <div className="fw-semibold">{request.shopName}</div>
                                    {request.shopAddress && (
                                        <div className="text-muted small">{request.shopAddress}</div>
                                    )}
                                </td>
                                <td className="small">
                                    {request.streetAddress && <div>{request.streetAddress}</div>}
                                    {request.provinceState && <div>{request.provinceState}</div>}
                                    {request.country && <div>{request.country}</div>}
                                    {!request.streetAddress && !request.provinceState && !request.country && "-"}
                                </td>
                                <td className="small">
                                    {request.contactPerson && <div>{request.contactPerson}</div>}
                                    {request.contactPhone && <div>{request.contactPhone}</div>}
                                    {request.contactEmail && <div>{request.contactEmail}</div>}
                                    {!request.contactPerson && !request.contactPhone && !request.contactEmail && "-"}
                                </td>
                                {activeTab === "registration" ? (
                                    <td className="small text-nowrap">{request.maskedCardNumber || "-"}</td>
                                ) : (
                                    <td className="small" style={{ maxWidth: "240px" }}>
                                        {request.reason ? (
                                            <>
                                                <div className="text-truncate mb-1" style={{ maxWidth: "220px" }}>
                                                    {request.reason}
                                                </div>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0"
                                                    onClick={() => setReasonRequest(request)}
                                                >
                                                    View full reason
                                                </Button>
                                            </>
                                        ) : "-"}
                                    </td>
                                )}
                                <td className="small text-muted text-nowrap">{formatDate(request.createdAt)}</td>
                                <td>
                                    <div className="d-flex flex-column gap-2">
                                        {request.status !== "PENDING" && (
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                onClick={() => handleActionClick("pending", request)}
                                                disabled={actionLoading}
                                            >
                                                Set Pending
                                            </Button>
                                        )}
                                        {request.status !== "APPROVED" && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleActionClick("approve", request)}
                                                disabled={actionLoading}
                                            >
                                                Approve
                                            </Button>
                                        )}
                                        {request.status !== "REJECTED" && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleActionClick("reject", request)}
                                                disabled={actionLoading}
                                            >
                                                Reject
                                            </Button>
                                        )}
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleActionClick("delete", request)}
                                            disabled={actionLoading}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                                {loading ? "Loading..." : "No trespass requests found."}
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                        <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                        {[...Array(totalPages)].map((_, i) => {
                            if (i < page - 2 || i > page + 2) return null;
                            return (
                                <Pagination.Item
                                    key={i}
                                    active={i === page}
                                    onClick={() => handlePageChange(i)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            );
                        })}
                        <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                    </Pagination>
                </div>
            )}

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmBody}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant={
                            confirmAction.type === "approve"
                                ? "success"
                                : confirmAction.type === "pending"
                                    ? "warning"
                                    : "danger"
                        }
                        onClick={handleConfirm}
                        disabled={actionLoading}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            "Confirm"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={Boolean(reasonRequest)} onHide={() => setReasonRequest(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Exemption Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {reasonRequest?.reason || "No reason provided."}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setReasonRequest(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TrespassRequestManager;
